import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Inbox, LifeBuoy, Search } from 'lucide-react';

import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { MetricTile } from '@/components/ui/MetricTile';
import { Pagination } from '@/components/ui/Pagination';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Table, type Column } from '@/components/ui/Table';
import {
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_OPTIONS,
} from '@/constants/support';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TicketPriority, TicketSummary } from '@/types/support';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import {
  LIVE_TICKETS,
  platformSupportApi,
  type TicketScopeParams,
  type TicketStatusFilter,
} from '../api/support.api';
import { TicketScopeFilters } from '../components/TicketScopeFilters';
import { TicketTrendChart } from '../components/TicketTrendChart';

const PAGE_SIZE = 20;

const STATUS_FILTER = [
  { value: '', label: 'Every status' },
  { value: LIVE_TICKETS, label: 'Not yet resolved' },
  ...TICKET_STATUS_OPTIONS,
];
const PRIORITY_FILTER = [{ value: '', label: 'Every priority' }, ...TICKET_PRIORITY_OPTIONS];

/**
 * The screen's filters, as they sit in the address bar. State, school, person
 * and dates are flat here and gathered into `TicketScopeParams` for the
 * components that share them.
 */
type Filters = {
  page: number;
  search: string;
  status: TicketStatusFilter | '';
  priority: TicketPriority | '';
  state: string;
  institute: string;
  raisedBy: string;
  from: string;
  to: string;
};

const DEFAULT_FILTERS: Filters = {
  page: 1,
  search: '',
  status: '',
  priority: '',
  state: '',
  institute: '',
  raisedBy: '',
  from: '',
  to: '',
};

/** A status or priority in a pasted link that is not a real one shows every ticket instead. */
const FILTER_OPTIONS = {
  allowed: {
    status: [LIVE_TICKETS, ...Object.keys(TICKET_STATUS_LABEL)],
    priority: Object.keys(TICKET_PRIORITY_LABEL),
  },
};

const COLUMNS: Column<TicketSummary>[] = [
  {
    key: 'ticket',
    header: 'Ticket',
    render: (row) => (
      <div className="min-w-0">
        <p className="font-mono text-xs text-ink-3">{row.ticketNumber}</p>
        <p className="truncate font-[560] text-ink">{row.subject}</p>
      </div>
    ),
  },
  {
    key: 'institute',
    header: 'Institute',
    render: (row) => (
      <div className="min-w-0">
        <Link
          to={`/platform/institutes/${row.institute.uuid}`}
          // The row opens the ticket; this opens the customer instead.
          onClick={(event) => event.stopPropagation()}
          className="truncate rounded-field font-[560] text-ink transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
        >
          {row.institute.displayName}
        </Link>
        <p className="truncate text-xs text-ink-3">{row.raisedBy.name}</p>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <div className="flex flex-wrap items-center gap-1.5">
        <TicketStatusBadge status={row.status} />
        <TicketPriorityBadge priority={row.priority} />
      </div>
    ),
  },
  {
    key: 'assigned',
    header: 'Owner',
    hideOnMobile: true,
    render: (row) =>
      row.assignedTo ? (
        <span className="text-ink-2">{row.assignedTo.name}</span>
      ) : (
        <span className="italic text-ink-3">Unassigned</span>
      ),
  },
  {
    key: 'activity',
    header: 'Last update',
    hideOnMobile: true,
    render: (row) => <span className="whitespace-nowrap text-ink-2">{formatDateTime(row.lastActivityAt)}</span>,
  },
];

/**
 * Every institute's support tickets, for whoever is on the desk.
 *
 * Urgent first, then whatever moved most recently. The four counts across the
 * top cover every live ticket whatever the filters say, and each one is a
 * shortcut to the filter that shows it.
 */
export function SupportQueuePage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const { page, status, priority, search } = filters;
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  /** State, school, person and dates — shared with the chart and the counts. */
  const scopeFilters: TicketScopeParams = {
    state: filters.state || undefined,
    institute: filters.institute || undefined,
    raisedBy: filters.raisedBy || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
  const scope: TicketScopeParams = { ...scopeFilters, search: debouncedSearch || undefined };

  const filterOptions = useApiQuery(() => platformSupportApi.filterOptions(), []);
  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      platformSupportApi.list({
        ...scope,
        page,
        limit: PAGE_SIZE,
        status: status || undefined,
        priority: priority || undefined,
      }),
    [
      page,
      status,
      priority,
      debouncedSearch,
      scopeFilters.state,
      scopeFilters.institute,
      scopeFilters.raisedBy,
      scopeFilters.from,
      scopeFilters.to,
    ],
  );

  const isScoped = Boolean(
    scopeFilters.state || scopeFilters.institute || scopeFilters.raisedBy || scopeFilters.from || scopeFilters.to || debouncedSearch,
  );

  const counts = data?.counts;

  /** Every filter change starts again from the first page. */
  function updateFilter(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  /**
   * A count tile that lists exactly what it counts: the live statuses, the
   * same set the server counts over, rather than every status.
   */
  function quickFilter(next: { priority?: TicketPriority | '' }) {
    updateFilter({ status: LIVE_TICKETS, priority: next.priority ?? '' });
  }

  const showingLive = status === LIVE_TICKETS;
  const tiles = [
    { label: 'Open', value: counts?.open, icon: <Inbox className="h-4 w-4" />, tone: 'indigo' as const, apply: () => quickFilter({}), active: showingLive && !priority },
    { label: 'Urgent', value: counts?.urgent, icon: <AlertTriangle className="h-4 w-4" />, tone: 'rose' as const, apply: () => quickFilter({ priority: 'URGENT' }), active: showingLive && priority === 'URGENT' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <DeckPageHeader
        eyebrow="Support"
        title="Support tickets"
        description="What institutes have asked us for help with — urgent first, then most recent."
      />

      {/* Metric Tiles row */}
      <div className="grid grid-cols-2 gap-3.5 sm:gap-4 mx-1">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.apply}
            aria-pressed={tile.active}
            className={cn(
              'group relative w-full rounded-2xl text-left transition-all duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line cursor-pointer',
              tile.active ? 'ring-2 ring-accent shadow-sm' : 'hover:shadow-xs',
            )}
          >
            <MetricTile
              label={tile.label}
              icon={tile.icon}
              tone={tile.tone}
              isLoading={!counts}
              value={tile.value ?? 0}
              hint={tile.label === 'Open' ? (isScoped ? 'For these filters' : 'Not yet resolved') : 'Of the open tickets'}
              className="h-full"
            />
          </button>
        ))}
      </div>

      {/* Scope filters card */}
      <section className="card flex flex-col gap-3.5 p-4 sm:p-5 shadow-xs border border-line bg-surface" aria-labelledby="scope-heading">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-3">
          <h2 id="scope-heading" className="lbl">
            Filter tickets
          </h2>
          {counts && (
            <p className="text-xs text-ink-2" role="status">
              <span className="font-[650] text-ink">{counts.raised}</span> raised ·{' '}
              <span className="font-[650] text-ink">{counts.open}</span> open
              {isScoped ? ' for these filters' : ' across every school'}
            </p>
          )}
        </div>
        <TicketScopeFilters
          value={scopeFilters}
          options={filterOptions.data}
          onChange={(next) =>
            updateFilter({
              state: next.state ?? '',
              institute: next.institute ?? '',
              raisedBy: next.raisedBy ?? '',
              from: next.from ?? '',
              to: next.to ?? '',
            })
          }
        />
        {filterOptions.error && (
          <p className="text-xs text-warn">The school and person lists could not be loaded. Reload to try again.</p>
        )}
      </section>

      {/* Table section: Quick filters & Table */}
      <section className="card flex flex-col gap-4 p-4 sm:p-5 shadow-xs border border-line bg-surface overflow-hidden">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Input
            label="Search"
            placeholder="Ticket no., subject or institute"
            leftIcon={<Search className="h-4 w-4" />}
            value={search}
            onChange={(event) => updateFilter({ search: event.target.value })}
          />
          <Select
            label="Status"
            options={STATUS_FILTER}
            value={status}
            onChange={(event) => updateFilter({ status: event.target.value as TicketStatusFilter | '' })}
          />
          <Select
            label="Priority"
            options={PRIORITY_FILTER}
            value={priority}
            onChange={(event) =>
              updateFilter({ priority: event.target.value as TicketPriority | '' })
            }
          />
          <DatePicker
            label="Raised from"
            value={scopeFilters.from ?? ''}
            max={scopeFilters.to}
            onChange={(event) => updateFilter({ from: event.target.value })}
          />
          <DatePicker
            label="Raised to"
            value={scopeFilters.to ?? ''}
            min={scopeFilters.from}
            onChange={(event) => updateFilter({ to: event.target.value })}
          />
        </div>

        {error ? (
          <QueryError title="Could not load tickets" error={error} onRetry={refetch} />
        ) : (
          <div className="overflow-hidden rounded-xl border border-line">
            <Table
              variant="plain"
              columns={COLUMNS}
              rows={data?.items ?? []}
              rowKey={(row) => row.uuid}
              isLoading={isLoading}
              onRowClick={(row) => navigate(`/platform/support/${row.uuid}`)}
              empty={
                <EmptyState
                  icon={<LifeBuoy className="h-5 w-5" />}
                  title="No tickets here"
                  description={
                    status || priority || isScoped
                      ? 'Nothing matches these filters. Clear them to see every ticket.'
                      : 'When an institute asks for help, the ticket appears here.'
                  }
                />
              }
            />
            {data && (
              <Pagination
                meta={data.meta}
                onPageChange={(next) => updateFilter({ page: next })}
                itemLabel="tickets"
              />
            )}
          </div>
        )}
      </section>

      {/* Trend chart placed below table */}
      <TicketTrendChart scope={scope} />
    </div>
  );
}
